import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import {
  useGetSession,
  getGetSessionQueryKey,
} from "@workspace/api-client-react";
import { useOfflineSync } from "@/hooks/use-offline-sync";
import { emitMagicMoment } from "@/lib/magic-engine";

type OfflineSyncContextValue = ReturnType<typeof useOfflineSync>;

const OfflineSyncContext = createContext<OfflineSyncContextValue | null>(null);

// F4 Slice 4 — minimum interval between auth-transition-triggered
// drains. The in-flight guard inside `syncNow()` already coalesces
// concurrent calls; this ref-based throttle is a *belt* on top so
// fast-fire query refetches (e.g. window-focus + interval refetch
// landing in the same tick) don't queue two near-back-to-back drains.
const AUTH_DRAIN_THROTTLE_MS = 750;

export function OfflineSyncProvider({ children }: { children: ReactNode }) {
  const value = useOfflineSync();

  // Watch the session query for a null/false → true transition. This
  // catches sign-in flows that don't go through pages/sign-in.tsx
  // (token refresh, multi-tab sign-in, returning-from-OAuth) without
  // each call site having to remember to fire syncNow().
  //
  // The session query is already mounted by App.tsx's <Router>, so
  // this useGetSession() call rides the existing cache — no extra
  // network request is made just to subscribe.
  const session = useGetSession({
    query: {
      // Pass the canonical query key so this consumer rides the same
      // cache entry that App.tsx / AppLayout / pages/* already mount.
      // Without it, orval-generated hooks default to a per-call key
      // and you'd get a duplicate /api/session/me request on every
      // app boot.
      queryKey: getGetSessionQueryKey(),
      // We don't drive any UI off of this read; we only need React
      // to re-render when authenticated flips. Default staleTime is
      // fine — the goal is reactivity, not freshness.
      notifyOnChangeProps: ["data"],
      retry: false,
    },
  });
  const prevAuthRef = useRef<boolean | undefined>(undefined);
  const lastDrainAtRef = useRef<number>(0);
  const prevPendingRef = useRef<number | null>(null);

  useEffect(() => {
    const cur = session.data?.authenticated === true;
    const prev = prevAuthRef.current;
    // Update the ref FIRST so an early return below still records the
    // transition we observed. This is what makes the very first
    // render a no-op even if the user lands already-authenticated:
    // we want the *real* unauth → auth transition, not the initial
    // "we just learned the session shape" tick.
    prevAuthRef.current = cur;
    if (prev !== false || cur !== true) return;

    const now = Date.now();
    if (now - lastDrainAtRef.current < AUTH_DRAIN_THROTTLE_MS) return;
    lastDrainAtRef.current = now;
    void value.syncNow();
  }, [session.data?.authenticated, value]);

  // Magic Engine — Moment 6: fire when a queued sync batch succeeds
  // (pendingCount drops from > 0). We only fire once per successful drain.
  useEffect(() => {
    const prev = prevPendingRef.current;
    const cur = value.pendingCount;
    if (prev !== null && prev > 0 && cur === 0 && !value.isSyncing) {
      emitMagicMoment("queued-sync-succeeded");
    }
    prevPendingRef.current = cur;
  }, [value.pendingCount, value.isSyncing]);

  return (
    <OfflineSyncContext.Provider value={value}>
      {children}
    </OfflineSyncContext.Provider>
  );
}

export function useOfflineSyncContext(): OfflineSyncContextValue {
  const ctx = useContext(OfflineSyncContext);
  if (!ctx) {
    throw new Error(
      "useOfflineSyncContext must be used inside <OfflineSyncProvider>",
    );
  }
  return ctx;
}
